// Artist banner framing — shared by the public ArtistPage banner and the
// banner editors (artist dashboard + admin panel). Keep in sync with
// dashboard/src/lib/bannerFraming.ts (separate app, same file).

import { buildCloudinaryInfoUrl } from "@/lib/cloudinaryUrl";

/** Where the image sits in the banner strip. null framing = automatic
 *  (Cloudinary subject detection). x/y are 0..1 (CSS object-position),
 *  zoom 1..3 scales around that point. */
export interface CoverFraming {
  x: number;
  y: number;
  zoom: number;
}

export const DEFAULT_FRAMING: CoverFraming = { x: 0.5, y: 0.5, zoom: 1 };
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;

/** Automatic mode: the strip's aspect per breakpoint (banner is
 *  h-56 / sm:h-72 / md:h-80, full width), each cropped around the subject. */
export const AUTO_BREAKPOINTS: { media?: string; aspect: number }[] = [
  { media: "(min-width: 1600px)", aspect: 6 },
  { media: "(min-width: 1024px)", aspect: 4.5 },
  { media: "(min-width: 768px)", aspect: 2.8 },
  { media: "(min-width: 640px)", aspect: 2.4 },
  { aspect: 1.8 },
];

/** Editor previews. overlap/gradient are fractions of banner height covered
 *  by the profile card (-mt-20 → 80px) and the bottom fade (h-24 → 96px);
 *  cardWidth is the card's share of the banner width (max-w-5xl, padded). */
export const BANNER_PREVIEWS = [
  { id: "wide", aspect: 1920 / 320, overlap: 80 / 320, gradient: 96 / 320, cardWidth: 960 / 1920 },
  { id: "laptop", aspect: 1440 / 320, overlap: 80 / 320, gradient: 96 / 320, cardWidth: 960 / 1440 },
  { id: "phone", aspect: 390 / 224, overlap: 80 / 224, gradient: 96 / 224, cardWidth: 358 / 390 },
] as const;
export type BannerPreviewId = (typeof BANNER_PREVIEWS)[number]["id"];

/** The widest banner is 1920 CSS px. Source pixels left after zoom decide
 *  whether it stays crisp there (2880 ≈ 1.5x density) or goes soft. */
export type BannerSharpness = "great" | "good" | "soft" | "unknown";
export function bannerSharpness(sourceWidth: number | null | undefined, zoom = 1): BannerSharpness {
  if (!sourceWidth) return "unknown";
  const effective = sourceWidth / zoom;
  if (effective >= 2880) return "great";
  if (effective >= 1920) return "good";
  return "soft";
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * Converts a pointer drag (px) inside a frame into a new focal point, so the
 * image follows the pointer 1:1.
 *
 * With object-fit: cover, object-position p shifts the image by
 * p · overflow, and scale(z) around that same point adds (z−1) · frame.
 * So one unit of p moves the image z·overflow + (z−1)·frame px.
 */
export function dragFraming(
  start: CoverFraming,
  dx: number,
  dy: number,
  frame: { width: number; height: number },
  imageAspect: number
): CoverFraming {
  const coverW = Math.max(frame.width, frame.height * imageAspect);
  const coverH = Math.max(frame.height, frame.width / imageAspect);
  const spanX = start.zoom * (coverW - frame.width) + (start.zoom - 1) * frame.width;
  const spanY = start.zoom * (coverH - frame.height) + (start.zoom - 1) * frame.height;
  return {
    ...start,
    x: spanX > 1 ? clamp(start.x - dx / spanX, 0, 1) : start.x,
    y: spanY > 1 ? clamp(start.y - dy / spanY, 0, 1) : start.y,
  };
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

export function clampFraming(f: CoverFraming): CoverFraming {
  return {
    x: round3(clamp(f.x, 0, 1)),
    y: round3(clamp(f.y, 0, 1)),
    zoom: round3(clamp(f.zoom, MIN_ZOOM, MAX_ZOOM)),
  };
}

interface CloudinaryInfo {
  input: { width: number; height: number };
  resize?: { x: number; y: number; width: number; height: number }[];
}

/**
 * Asks Cloudinary for an image's original size and, when `aspect` is given,
 * the crop its automatic (g_auto) framing picks at that aspect — expressed
 * as the equivalent manual framing, so leaving auto mode starts from exactly
 * what was on screen. Resolves null for non-Cloudinary URLs or on failure.
 */
export async function fetchCloudinaryFraming(
  url: string,
  aspect?: number
): Promise<{ width: number; height: number; auto: CoverFraming | null } | null> {
  const infoUrl = buildCloudinaryInfoUrl(url, aspect);
  if (!infoUrl) return null;
  try {
    const res = await fetch(infoUrl);
    if (!res.ok) return null;
    const info = (await res.json()) as CloudinaryInfo;
    const { width, height } = info.input;
    const crop = info.resize?.[0];
    const auto = crop
      ? clampFraming({
          x: width > crop.width ? crop.x / (width - crop.width) : 0.5,
          y: height > crop.height ? crop.y / (height - crop.height) : 0.5,
          zoom: 1,
        })
      : null;
    return { width, height, auto };
  } catch {
    return null;
  }
}
